import React, { useMemo } from 'react';
import { Modal, Select, Input, Radio, Button, Space, Row, Col, DatePicker, Spin } from 'antd';
import dayjs from 'dayjs';
import './OrderModals.css';

// Debounce time in milliseconds
const DEBOUNCE_TIME = 150;

// Create debounced handler
const createDebouncedHandler = (handler) => {
  let timeoutId;
  return (e) => {
    const value = e.target.value;
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      // Create a synthetic event object with the value
      handler({ target: { value } });
    }, DEBOUNCE_TIME);
  };
};

const { Option } = Select;
const { TextArea } = Input;

const OrderModals = ({
  // Batch Modal props
  batchModalVisible,
  selectedBatchId,
  batches,
  fetchingBatches,
  onBatchModalCancel,
  onBatchModalOk,
  onBatchSelect,
  
  // Order Search Modal props
  searchModalVisible,
  searchOrderNumbers,
  onSearchModalCancel,
  onSearchModalOk,
  onSearchOrderNumbersChange,
  
  // Express Search Modal props
  expressSearchModalVisible,
  searchExpressNumbers,
  onExpressSearchModalCancel,
  onExpressSearchModalOk,
  onSearchExpressNumbersChange,

  // Batch Number Modal props
  batchSearchModalVisible,
  selectedBatchNumber,
  userBatches,
  newBatchNumber,
  onBatchSearchModalCancel,
  onBatchSearchModalOk,
  onBatchNumberSelect,
  onNewBatchNumberChange,
  onAddBatch,

  // Batch Search Modal props
  batchNumberSearchModalVisible,
  searchBatchNumber,
  onBatchNumberSearchModalCancel,
  onBatchNumberSearchModalOk,
  onSearchBatchNumberChange,
  userBatchesForSearch,

  // Date Range Modal props
  dateRangeModalVisible,
  dateRange,
  onDateRangeModalCancel,
  onDateRangeModalOk,
  onDateRangeChange,

  // Recipient Search Modal props
  recipientSearchModalVisible,
  searchRecipientName,
  onRecipientSearchModalCancel,
  onRecipientSearchModalOk,
  onSearchRecipientNameChange,

  // Status Filter Modal props
  statusFilterModalVisible,
  selectedStatus,
  onStatusFilterModalCancel,
  onStatusFilterModalOk,
  onStatusSelect,

  // Inspection Filter Modal props
  inspectionFilterModalVisible,
  selectedInspection,
  onInspectionFilterModalCancel,
  onInspectionFilterModalOk,
  onInspectionSelect,

  // Short Package Filter Modal props
  shortPackageFilterModalVisible,
  selectedShortPackage,
  onShortPackageFilterModalCancel,
  onShortPackageFilterModalOk,
  onShortPackageSelect,

  // Claim Filter Modal props
  claimFilterModalVisible,
  selectedClaim,
  onClaimFilterModalCancel,
  onClaimFilterModalOk,
  onClaimSelect,

  // Modified Filter Modal props
  modifiedFilterModalVisible,
  selectedModified,
  onModifiedFilterModalCancel,
  onModifiedFilterModalOk,
  onModifiedSelect,

  // Customer Search Modal props
  customerSearchModalVisible,
  selectedCustomer,
  customers,
  loadingCustomers,
  onCustomerSearchModalCancel,
  onCustomerSearchModalOk,
  onCustomerSelect,

  // Recipient Phone Search Modal props
  recipientPhoneSearchModalVisible,
  searchRecipientPhone,
  onRecipientPhoneSearchModalCancel,
  onRecipientPhoneSearchModalOk,
  onSearchRecipientPhoneChange,

  // Sender Search Modal props
  senderSearchModalVisible,
  searchSenderName,
  onSenderSearchModalCancel,
  onSenderSearchModalOk,
  onSearchSenderNameChange,

  // Sender Phone Search Modal props
  senderPhoneSearchModalVisible,
  searchSenderPhone,
  onSenderPhoneSearchModalCancel,
  onSenderPhoneSearchModalOk,
  onSearchSenderPhoneChange,
}) => {
  const presets = [
    {
      label: '最近一周',
      value: [dayjs().subtract(7, 'day'), dayjs()]
    },
    {
      label: '最近一个月',
      value: [dayjs().subtract(1, 'month'), dayjs()]
    },
    {
      label: '最近三个月',
      value: [dayjs().subtract(3, 'month'), dayjs()]
    }
  ];

  const statusOptions = [
    { label: '已创建', value: 'CREATED' },
    { label: '已打印', value: 'PRINTED' },
    { label: '运输中', value: 'SHIPPING' },
    // { label: '未审单', value: 'PENDING_REVIEW' },
    { label: '待清关', value: 'PENDING_CUSTOMS' },
    // { label: '已提货未过机', value: 'PICKED_UP' },
    // { label: '已出查验未放行', value: 'INSPECTED_PENDING' },
    { label: '已放行', value: 'RELEASED' },
    { label: '派送中', value: 'DELIVERING' },
    { label: '已送达', value: 'DELIVERED' }
  ];

  return (
    <>
      <Modal
        title="设置批次"
        open={batchModalVisible}
        onOk={onBatchModalOk}
        onCancel={onBatchModalCancel}
        okText="确认"
        cancelText="取消"
      >
        <Select
          style={{ width: '100%' }}
          placeholder="请选择批次"
          value={selectedBatchId}
          onChange={onBatchSelect}
          loading={fetchingBatches}
        >
          {(batches || []).map(batch => (
            <Option key={batch.id} value={batch.id}>
              {batch.batch_number} (订单数: {batch.order_count})
            </Option>
          ))}
        </Select>
      </Modal>

      <Modal
        title="搜索订单"
        open={searchModalVisible}
        onOk={onSearchModalOk}
        onCancel={onSearchModalCancel}
        okText="搜索"
        cancelText="取消"
      >
        <TextArea
          placeholder="请输入订单号，多个订单号可用换行或逗号分隔"
          defaultValue={searchOrderNumbers}
          onChange={useMemo(() => createDebouncedHandler(onSearchOrderNumbersChange), [onSearchOrderNumbersChange])}
          rows={4}
          style={{ marginBottom: 8 }}
        />
        <div style={{ color: '#888', fontSize: '12px' }}>
          提示：可以粘贴多个订单号，系统会自动识别
        </div>
      </Modal>

      <Modal
        title="搜索快递单号"
        open={expressSearchModalVisible}
        onOk={onExpressSearchModalOk}
        onCancel={onExpressSearchModalCancel}
        okText="搜索"
        cancelText="取消"
      >
        <TextArea
          placeholder="请输入快递单号，多个快递单号可用换行或逗号分隔"
          defaultValue={searchExpressNumbers}
          onChange={useMemo(() => createDebouncedHandler(onSearchExpressNumbersChange), [onSearchExpressNumbersChange])}
          rows={4}
          style={{ marginBottom: 8 }}
        />
        <div style={{ color: '#888', fontSize: '12px' }}>
          提示：可以粘贴多个快递单号，系统会自动识别
        </div>
      </Modal>

      <Modal
        title="选择批次号"
        open={batchSearchModalVisible}
        onOk={onBatchSearchModalOk}
        onCancel={onBatchSearchModalCancel}
        okText="确认"
        cancelText="取消"
      >
        <div style={{ marginBottom: 16 }}>
          <Space>
            <Input
              placeholder="请输入新批次号"
              value={newBatchNumber}
              onChange={onNewBatchNumberChange}
              style={{ width: 200 }}
            />
            <Button type="primary" onClick={onAddBatch}>添加</Button>
          </Space>
        </div>
        <Radio.Group 
          onChange={e => onBatchNumberSelect(e.target.value)} 
          value={selectedBatchNumber}
          style={{ width: '100%' }}
        >
          <Row gutter={[8, 8]}>
            {(userBatches || []).map(batch => (
              <Col span={6} key={batch.id}>
                <Radio 
                  value={batch.batch_number}
                  style={{ 
                    width: '100%',
                    padding: '4px',
                    marginRight: 0,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {batch.batch_number}
                </Radio>
              </Col>
            ))}
          </Row>
        </Radio.Group>
      </Modal>

      <Modal
        title="搜索批次号"
        open={batchNumberSearchModalVisible}
        onOk={onBatchNumberSearchModalOk}
        onCancel={onBatchNumberSearchModalCancel}
        okText="确认"
        cancelText="取消"
      >
        <Input
          placeholder="请输入批次号"
          value={searchBatchNumber}
          onChange={e => onSearchBatchNumberChange(e.target.value)}
          style={{ width: '100%' }}
        />
      </Modal>

      <Modal
        title="导入时间"
        open={dateRangeModalVisible}
        onOk={onDateRangeModalOk}
        onCancel={onDateRangeModalCancel}
        okText="搜索"
        cancelText="取消"
      >
        <DatePicker.RangePicker
          style={{ width: '100%' }}
          value={dateRange}
          onChange={onDateRangeChange}
          format="YYYY-MM-DD"
          presets={presets}
          locale={{
            lang: {
              locale: 'zh_CN',
              rangePlaceholder: ['开始日期', '结束日期']
            }
          }}
        />
      </Modal>

      <Modal
        title="搜索收件人"
        open={recipientSearchModalVisible}
        onOk={onRecipientSearchModalOk}
        onCancel={onRecipientSearchModalCancel}
        okText="搜索"
        cancelText="取消"
      >
        <TextArea
          placeholder="请输入收件人姓名，多个姓名可用换行或逗号分隔"
          defaultValue={searchRecipientName}
          onChange={useMemo(() => createDebouncedHandler(onSearchRecipientNameChange), [onSearchRecipientNameChange])}
          rows={4}
          style={{ marginBottom: 8 }}
        />
        <div style={{ color: '#888', fontSize: '12px' }}>
          提示：可以粘贴多个收件人姓名，系统会自动识别
        </div>
      </Modal>

      <Modal
        title="筛选状态"
        open={statusFilterModalVisible}
        onOk={onStatusFilterModalOk}
        onCancel={onStatusFilterModalCancel}
        okText="确认"
        cancelText="取消"
        width={750}
      >
        <Radio.Group 
          onChange={e => onStatusSelect(e.target.value)} 
          value={selectedStatus}
          style={{ width: '100%' }}
        >
          <Space size="middle" style={{ width: '100%' }}>
            {statusOptions.map(option => (
              <Radio key={option.value} value={option.value}>
                {option.label}
              </Radio>
            ))}
          </Space>
        </Radio.Group>
      </Modal>

      <Modal
        title="查验件筛选"
        open={inspectionFilterModalVisible}
        onOk={onInspectionFilterModalOk}
        onCancel={onInspectionFilterModalCancel}
        okText="确认"
        cancelText="取消"
      >
        <Radio.Group 
          onChange={e => onInspectionSelect(e.target.value)} 
          value={selectedInspection}
          style={{ width: '100%' }}
        >
          <Space size="large">
            <Radio value={true}>是</Radio>
            <Radio value={false}>否</Radio>
          </Space>
        </Radio.Group>
      </Modal>

      <Modal
        title="短装件筛选"
        open={shortPackageFilterModalVisible}
        onOk={onShortPackageFilterModalOk}
        onCancel={onShortPackageFilterModalCancel}
        okText="确认"
        cancelText="取消"
      >
        <Radio.Group 
          onChange={e => onShortPackageSelect(e.target.value)} 
          value={selectedShortPackage}
          style={{ width: '100%' }}
        >
          <Space size="large">
            <Radio value={true}>是</Radio>
            <Radio value={false}>否</Radio>
          </Space>
        </Radio.Group>
      </Modal>

      <Modal
        title="理赔件筛选"
        open={claimFilterModalVisible}
        onOk={onClaimFilterModalOk}
        onCancel={onClaimFilterModalCancel}
        okText="确认"
        cancelText="取消"
      >
        <Radio.Group 
          onChange={e => onClaimSelect(e.target.value)} 
          value={selectedClaim}
          style={{ width: '100%' }}
        >
          <Space size="large">
            <Radio value={true}>是</Radio>
            <Radio value={false}>否</Radio>
          </Space>
        </Radio.Group>
      </Modal>

      <Modal
        title="信息修改件筛选"
        open={modifiedFilterModalVisible}
        onOk={onModifiedFilterModalOk}
        onCancel={onModifiedFilterModalCancel}
        okText="确认"
        cancelText="取消"
      >
        <Radio.Group 
          onChange={e => onModifiedSelect(e.target.value)} 
          value={selectedModified}
          style={{ width: '100%' }}
        >
          <Space size="large">
            <Radio value={true}>是</Radio>
            <Radio value={false}>否</Radio>
          </Space>
        </Radio.Group>
      </Modal>

      <Modal
        title="选择客户"
        open={customerSearchModalVisible}
        onOk={onCustomerSearchModalOk}
        onCancel={onCustomerSearchModalCancel}
        okText="确认"
        cancelText="取消"
        width={750}
      >
        <Spin spinning={loadingCustomers}>
          <Radio.Group 
            onChange={e => onCustomerSelect(e.target.value)} 
            value={selectedCustomer}
            style={{ width: '100%' }}
          >
            <Row gutter={[8, 8]}>
              {(customers || []).map(customer => (
                <Col span={6} key={customer.id}>
                  <Radio 
                    value={customer.id}
                    style={{ 
                      width: '100%',
                      padding: '4px',
                      marginRight: 0,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {customer.company_name || customer.name}
                  </Radio>
                </Col>
              ))}
            </Row>
          </Radio.Group>
        </Spin>
      </Modal>

      <Modal
        title="搜索收件人电话"
        open={recipientPhoneSearchModalVisible}
        onOk={onRecipientPhoneSearchModalOk}
        onCancel={onRecipientPhoneSearchModalCancel}
        okText="搜索"
        cancelText="取消"
      >
        <TextArea
          placeholder="请输入收件人电话，多个电话可用换行或逗号分隔"
          defaultValue={searchRecipientPhone}
          onChange={useMemo(() => createDebouncedHandler(onSearchRecipientPhoneChange), [onSearchRecipientPhoneChange])}
          rows={4}
          style={{ marginBottom: 8 }}
        />
        <div style={{ color: '#888', fontSize: '12px' }}>
          提示：可以粘贴多个收件人电话，系统会自动识别
        </div>
      </Modal>

      <Modal
        title="搜索寄件人"
        open={senderSearchModalVisible}
        onOk={onSenderSearchModalOk}
        onCancel={onSenderSearchModalCancel}
        okText="搜索"
        cancelText="取消"
      >
        <TextArea
          placeholder="请输入寄件人姓名，多个姓名可用换行或逗号分隔"
          defaultValue={searchSenderName}
          onChange={useMemo(() => createDebouncedHandler(onSearchSenderNameChange), [onSearchSenderNameChange])}
          rows={4}
          style={{ marginBottom: 8 }}
        />
        <div style={{ color: '#888', fontSize: '12px' }}>
          提示：可以粘贴多个寄件人姓名，系统会自动识别
        </div>
      </Modal>

      <Modal
        title="搜索寄件人电话"
        open={senderPhoneSearchModalVisible}
        onOk={onSenderPhoneSearchModalOk}
        onCancel={onSenderPhoneSearchModalCancel}
        okText="搜索"
        cancelText="取消"
      >
        <TextArea
          placeholder="请输入寄件人电话，多个电话可用换行或逗号分隔"
          defaultValue={searchSenderPhone}
          onChange={useMemo(() => createDebouncedHandler(onSearchSenderPhoneChange), [onSearchSenderPhoneChange])}
          rows={4}
          style={{ marginBottom: 8 }}
        />
        <div style={{ color: '#888', fontSize: '12px' }}>
          提示：可以粘贴多个寄件人电话，系统会自动识别
        </div>
      </Modal>
    </>
  );
};

export default OrderModals;
